import { LitElement, html, nothing, css, unsafeCSS } from 'lit';
import { ATTRIBUTES, DEFAULT_INPUT_PROPERTIES, INPUT_TYPES } from './constants';
import { ref, createRef } from 'lit/directives/ref.js';
import { FormValidationController } from '@/controllers/FormValidationController';
import { live } from 'lit/directives/live.js';
import styles from './input_reset.scss?inline;';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitInput extends LitElement {
    static formAssociated = true;

    static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        ...DEFAULT_INPUT_PROPERTIES,
        type: { type: String },
        clearable: { type: Boolean },
        autofocus: { type: Boolean },
        disableSubmit: { type: Boolean, attribute: ATTRIBUTES.DISABLE_SUBMIT },
        autocomplete: { type: String },
        defaultSlotPosition: {
            type: String,
            attribute: 'default-slot-position',
        },
        size: { type: String },
        pattern: { type: String, attribute: 'pattern' },
        min: { type: String, attribute: 'min' },
        max: { type: String, attribute: 'max' },
        step: { type: String, attribute: 'step' },
        minLength: { type: Number, attribute: 'minlength' },
        maxLength: { type: Number, attribute: 'maxlength' },
    };

    constructor() {
        super();

        this._internals = this.attachInternals();
        this._validationController = new FormValidationController(this, {
            getValue: () => this.value,
            getInputRef: () => this.inputRef,
        });

        this._value = '';
        this.type = INPUT_TYPES.TEXT;
        this.name = '';
        this.label = null;
        this.placeholder = null;
        this.required = false;
        this.disabled = false;
        this.clearable = false;
        this.autofocus = false;
        this.helperText = null;
        this.errorText = null;
        this.autocomplete = 'on';
        this.defaultSlotPosition = null;
        this.inputRef = createRef();
        this.styleRef = createRef();
        this.size = null;
        this.error = false;
        this.form = null;
        this._defaultValue = '';

        this.pattern = null;
        this.min = null;
        this.max = null;
        this.required = false;
        this.step = null;
        this.minLength = null;
        this.maxLength = null;
    }

    set value(value) {
        if (this.inputRef?.value) {
            this.inputRef.value.value = value;
        }

        this._internals?.setFormValue(value);
        this._value = value;

        // Keep ElementInternals aligned with the shadow input so form.checkValidity() / submit
        // (which run before our submit listener) see the real state.
        if (this.inputRef?.value) {
            this._validationController.validate();
        }

        if (this.error) {
            this.checkValidity(false);
        }
    }

    get value() {
        return this._value;
    }

    get placeholder() {
        return this._placeholder;
    }

    set placeholder(placeholder) {
        this._placeholder = placeholder;
    }

    get selectionStart() {
        return this.inputRef.value?.selectionStart || 0;
    }

    firstUpdated() {
        super.firstUpdated();
        this._defaultValue = this.value || '';
        // formAssociatedCallback often runs before the shadow input exists; sync ElementInternals once the ref is live.
        this._validationController.validate();
    }

    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    formAssociatedCallback(form) {
        this._validationController.removeInputHandlers();
        this._internals?.setFormValue(this.value);
        this.form = form;
        this._validationController.validate();
        this._validationController.setInputHandlers();
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formResetCallback() {
        this.value = this._defaultValue;
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    reportValidity() {
        return this.checkValidity(true);
    }

    focus(options) {
        this.inputRef.value?.focus(options);
    }

    _handleInput(e) {
        this.value = e.target.value;
    }

    _handleKeydown(e) {
        if (this.disableSubmit) {
            return;
        }

        if (e.defaultPrevented || e.key !== 'Enter') {
            return;
        }

        if (this.inputRef?.value) {
            this.value = this.inputRef.value.value;
        }

        if (this._internals?.form) {
            if (this._internals?.form.checkValidity()) {
                this._internals?.form.requestSubmit();
            } else {
                this._internals?.form.reportValidity();
            }
        }
    }

    _handleChange(e) {
        if (e.composed) {
            return;
        }

        this.dispatchEvent(
            new CustomEvent('change', {
                bubbles: true,
                composed: true,
                detail: {
                    originalEvent: e,
                    value: this.value,
                },
            })
        );
    }

    setSelectionRange(...args) {
        return this.inputRef.value?.setSelectionRange(...args);
    }

    calculatePlaceholderShow() {
        return this.styleRef.value?.calculatePlaceholderShow();
    }

    calculateSmallLabel() {
        return this.styleRef.value?.calculateSmallLabel();
    }

    render() {
        return html`
            <sotbit-input-style
                size="${this.size}"
                type="${this.type}"
                ?error="${this.error}"
                error-text="${this.errorText || nothing}"
                helper-text="${this.helperText || nothing}"
                label="${this.label || nothing}"
                ?clearable="${this.clearable}"
                ${ref(this.styleRef)}
                default-slot-position="${this.defaultSlotPosition || nothing}"
                @change="${this._handleChange}"
                @input="${this._handleInput}"
                @keydown="${this._handleKeydown}"
                no-readonly-style
            >
                <slot name="icon-left" slot="icon-left"></slot>
                <slot name="clear-button" slot="clear-button">
                    <sotbit-icon icon="close" size="m" variant="secondary" pointer class="clear-button"></sotbit-icon>
                </slot>
                <input
                    type="${this.type}"
                    name="${this.name}"
                    slot="input"
                    part="input"
                    ${ref(this.inputRef)}
                    .value="${live(this.value)}"
                    placeholder="${this.placeholder}"
                    ?disabled="${this.disabled}"
                    ?readonly="${this.readonly}"
                    ?required="${this.required}"
                    ?autofocus="${this.autofocus}"
                    autocomplete="${this.autocomplete || nothing}"
                    pattern="${this.pattern || nothing}"
                    min="${this.min || nothing}"
                    max="${this.max || nothing}"
                    step="${this.step || nothing}"
                    minlength="${this.minLength || nothing}"
                    maxlength="${this.maxLength || nothing}"
                />
                <slot name="icon-right" slot="icon-right"></slot>
                ${this.defaultSlotPosition ? html` <slot></slot>` : ''}
            </sotbit-input-style>
        `;
    }
}
